<template>
  <div class="tw:!border tw:!border-gray-300 tw:!first:rounded-t-lg tw:!last:rounded-b-lg tw:not-last:!border-b-0">
    <div class="tw:!flex tw:!bg-white">
      <div class="tw:g!rid tw:!place-content-center" v-expandable>
        <img class="tw:size-14 tw:md:!size-16 tw:!mx-3"
          :src="`https://static.tripcdn.com/packages/flight/airline-logo/latest/airline_logo/3x/${flight.company.marketing.code.toLocaleLowerCase()}.webp`"
          alt="">
      </div>
      <div class="tw:!grid tw:!place-content-center tw:!w-[30%]" v-expandable>
        <Transition name="animation" mode="out-in">
          <div v-if="!expanded">
            <p class="tw:!font-semibold tw:!p-0">{{ formatter.time(flight.departure.time) }} - {{ formatter.time(flight.arrival.time) }}</p>
            <p class="tw:!text-sm tw:!opacity-60">{{ flight.company.marketing.name }}</p>
          </div>
          <div v-else>
            <p class="tw:!font-semibold tw:!p-0">
              {{ dayjs(formatter.date(flight.departure.date), 'DD/MM/YYYY').format('MMMM D, YYYY') }}
            </p>
            <p v-if="flight.duration" class="tw:!text-sm tw:!opacity-60">
              {{ (flight.duration / 60) | 0 }} hrs {{ flight.duration % 60 }} mins
            </p>
          </div>
        </Transition>
      </div>

      <div class="tw:!grid tw:!w-[25%] tw:!pl-5" v-expandable>
        <div class="tw:!my-auto">
          <p class="tw:!font-semibold tw:!p-0">{{ flight.arrival.iata }}</p>
          <p class="tw:!text-sm tw:!opacity-60">{{ flight.stops ? `${flight.stops} Stops` : 'Nonstop' }}</p>
        </div>
      </div>

      <div class="tw:!hidden tw:!w-[20%] tw:md:!grid" v-expandable>
        <div class="tw:!my-auto">
          <p class="tw:!font-semibold tw:!p-0">{{ flight.arrival.name.split(',').pop() }}</p>
          <p class="tw:!text-sm tw:!opacity-60">{{ flight.company.marketing.code }} {{ flight.segments[0]?.number }}</p>
        </div>
      </div>

      <slot name="select" v-bind="{ flight, select }">
        <div class="tw:!grid tw:!place-content-center tw:!w-[25%]">
          <button color="primary" raised @click="emit('select', flight)">Select</button>
        </div>
      </slot>
    </div>

    <Transition
      @enter="transition.enter"
      @after-enter="transition.after"
      @leave="transition.leave"
    >
      <div class="custom-grow-box" v-if="expanded">
        <div class="tw:!px-3 tw:!py-2">
          <div class="tw:!mb-3 tw:!last:border-b-0 tw:!last:mb-0" v-for="item, i in flight.segments">
            <div class="tw:!flex tw:!space-x-3">
              <div class="tw:!flex tw:!flex-col tw:!py-2">
                <span class="tw:!border-2 tw:!size-3 tw:!rounded-full" />
                <span class="tw:!border-l-2 tw:!border-dotted tw:!h-full tw:!my-1 tw:!flex-1 tw:!self-center" />
                <span class="tw:!border-2 tw:!size-3 tw:!rounded-full" />
              </div>
              <div>
                <div class="tw:!font-semibold">{{ formatter.time(item.departure.time) }} - {{ item.departure.name }} ({{ item.departure.iata }})</div>
                <div class="tw:!text-sm tw:!font-normal tw:!opacity-60 tw:!py-2">Travel time: {{ (item.duration / 60) | 0 }} hr {{ item.duration % 60 }} min</div>
                <div class="tw:!font-semibold">{{ formatter.time(item.arrival.time) }} - {{ item.arrival.name }} ({{ item.arrival.iata }})</div>
              </div>
            </div>

            <div class="tw:!my-2">
              <div class="tw:!text-xs tw:!opacity-60 tw:!pl-6">
                {{ item.company.marketing.name }}
                • {{ item.equipment.name }}
                • {{ item.company.marketing.code }} {{ item.number }}
              </div>
            </div>

            <Variable v-if="flight.segments[i + 1]" :set="flight.segments[i + 1]!" v-slot="next">
              <div class="tw:!border-t tw:!border-dashed">
                <div v-if="item.departure.date !== next.departure.date" class="tw:!pl-6 tw:!pt-2">
                  <span class="tw:!font-medium tw:!opacity-80">
                    Departure • {{ dayjs(formatter.date(next.departure.date), 'DD/MM/YYYY').format('MMMM D, YYYY') }}
                  </span>
                </div>
              </div>
            </Variable>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script lang="ts">
import mitt, { type Emitter } from 'mitt'
import Variable from './Variable.vue'
import { formatter } from '~/utils'
type MittEvents = {
  expand: {id: string, expanded: boolean}
}

const global = new Map<string|symbol, Emitter<MittEvents>>()

const useGroupSignal = (name: string|symbol = '#__global') => {
  const emitter = global.get(name) ?? mitt<MittEvents>()

  if (! global.has(name)) {
    global.set(name, emitter)
  }

  return emitter
}
</script>

<script lang="ts" setup>
import dayjs from 'dayjs'
import { onBeforeUnmount, onMounted, ref } from 'vue'
import type { IFlight } from '~/types'
import customParseFormat from 'dayjs/plugin/customParseFormat'

dayjs.extend(customParseFormat)

const props = defineProps<{ flight: IFlight, group?: string|symbol }>()

const emit = defineEmits<{ select: [flight: IFlight] }>()

const id = Math.random().toString(36).substring(2, 12)
const expanded = ref(false)
const emitter = useGroupSignal(props.group)


const vExpandable = {
  mounted(e: HTMLElement) {
    e.classList.add('tw:!cursor-pointer')
    e.addEventListener('click', () => {
      emitter.emit('expand', {
        id, expanded: expanded.value = !expanded.value
      })
    })
  }
}

const select = (flight: IFlight) => emit('select', flight)

const transition = {
  enter(e: any) {
    e.style.height = '0'
    // Force a reflow to trigger the transition
    e.offsetHeight 
    e.style.height = e.scrollHeight + 'px'
  },
  after(e: any) { e.style.height = 'auto' },
  leave(e: any) {
    e.style.height = e.scrollHeight + 'px'
    e.offsetHeight // force reflow
    e.style.height = '0'
  }
}

onMounted(() => {
  const listener = (data: MittEvents['expand']) => {
    if (id === data.id || !data.expanded) {
      return
    }

    expanded.value = false
  }

  emitter.on('expand', listener)

  onBeforeUnmount(() =>  emitter.off('expand', listener))
})
</script>

<style scoped>
.custom-grow-box {
  overflow: hidden;
  transition: height 0.4s ease;
}

.animation-enter-active,
.animation-leave-active {
  transition: all 0.2s ease;
}

.animation-enter-from,
.animation-leave-to {
  opacity: 0;
  transform: translateY(5px) scale(.96);
}
</style>